// Group 4 blog posts: financing/company-shutdown explainers, recreated from
// mckayroofingsolar.com source pages. Every image below is an asset that
// already exists in this project.

import type { BlogPost } from "./blogPosts";

import heroInsuranceClaims from "@/assets/service-heroes/roof-insurance-claims.jpg.asset.json";
import roofRepairHero from "@/assets/service-heroes/roof-repair-hero.jpg.asset.json";
import solarPanelReplacement from "@/assets/service-heroes/solar-panel-replacement.jpg.asset.json";
import solarInverterInstallation from "@/assets/service-heroes/solar-inverter-installation.jpg.asset.json";
import commercialSolar from "@/assets/service-heroes/commercial-solar.jpg.asset.json";
import solarInstallation from "@/assets/service-heroes/solar-installation.jpg.asset.json";

export const group4Posts: BlogPost[] = [
  {
    slug: "hero-pace-loan-customers",
    title: "HERO & Ygrene PACE Loans: What Happened Now | McKay",
    h1: "HERO and Ygrene PACE financing shut down. What does that mean for the roof or solar system you already paid for?",
    metaDescription:
      "Renovate America's HERO program went bankrupt and Ygrene stopped new PACE loans. Here is what that means for your property tax assessment and your roof.",
    keywords:
      "HERO PACE loan, Ygrene financing, Renovate America bankruptcy, PACE assessment San Diego, PACE roof inspection",
    category: "Solar Service",
    datePublished: "2026-09-06",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: heroInsuranceClaims.url,
      alt: "Homeowner reviewing property tax paperwork next to a Southern California roof financed through a PACE program",
      caption: "You paid for that roof through your property taxes. The program that arranged the loan is gone. The roof is still yours.",
    },
    intro: [
      "If you financed a roof, a solar array, or an HVAC system in San Diego County sometime in the last decade, there is a decent chance you used a PACE program with the name HERO or Ygrene attached to it. Both were everywhere here for years, bundled into the sales pitch at the kitchen table alongside the shingles or the panels.",
      "Both are also, in the form you signed up for, gone. Renovate America, the San Diego company behind HERO, filed for Chapter 11 bankruptcy in December 2020. Ygrene, the other major PACE name of that era, stopped funding new residential projects in 2022. Thousands of local roofs and solar systems were installed under programs that have since closed their doors, and homeowners are left with two separate questions: what happens to the assessment on my tax bill, and what happens to the roof itself.",
      "This post answers both, plainly and without spinning it into a sales pitch. McKay Roofing and Solar is not affiliated with Renovate America, HERO, or Ygrene, and nothing here is legal, tax, or financial advice. It is what we tell customers who call us confused about a program that stopped existing years after they signed the paperwork.",
    ],
    tldr: [
      "The HERO program's parent company, Renovate America, filed Chapter 11 bankruptcy in December 2020. Ygrene stopped funding new residential PACE projects in 2022.",
      "Your PACE assessment did not disappear. It rides on your property tax bill and continues to be collected by successor servicers regardless of what happened to the original company.",
      "PACE financing itself is not dead. Active, regulated programs still operate in California, and we work with Renew Financial when PACE genuinely fits a project.",
      "The real risk is an orphaned roof: the installer who did the PACE-financed work is often long gone, and nobody has inspected it since. A written [roof inspection](/services/roof-inspection) closes that gap.",
    ],
    sections: [
      {
        id: "what-actually-happened",
        h2: "What actually happened to HERO and Ygrene?",
        blocks: [
          {
            type: "p",
            text: "HERO stood for Home Energy Renovation Opportunity, and it was administered by Renovate America, a San Diego company. PACE, the broader category both programs belonged to, stands for Property Assessed Clean Energy: a way to finance energy upgrades through an assessment attached to the home's property taxes rather than a conventional loan.",
          },
          {
            type: "p",
            text: "Renovate America filed for Chapter 11 bankruptcy protection in December 2020, after years of scrutiny over how aggressively some contractors pitched the financing and how little income verification some of those loans required. Ygrene, the other big name in the same space, did not go through bankruptcy in the same way, but it stopped funding new residential PACE projects in 2022 and scaled its consumer-facing operations back sharply.",
          },
          {
            type: "callout",
            title: "Not affiliated, general information only",
            text: "McKay Roofing and Solar is not affiliated with Renovate America, HERO, or Ygrene, and this article is general information, not legal, tax, or financial advice. Questions about your specific loan or settlement eligibility belong with an attorney or the relevant program administrator.",
          },
        ],
        image: {
          src: roofRepairHero.url,
          alt: "Roof financed years ago through a PACE program showing wear that has never been reassessed",
          caption: "A roof financed through a program that no longer exists still needs the same maintenance every other roof does.",
        },
      },
      {
        id: "what-it-means-for-your-assessment",
        h2: "Does the bankruptcy erase what I still owe on my property taxes?",
        blocks: [
          {
            type: "p",
            text: "No. This is the part that surprises the most people. PACE obligations are structured as an assessment on the property itself, not a personal loan to you. Industry and court reporting on the Renovate America bankruptcy was consistent on this point: the bankruptcy did not erase outstanding assessments. They continue to be collected and serviced by successor entities through the normal county property tax bill.",
          },
          {
            type: "p",
            text: "In practical terms, that means the line item on your tax bill likely did not go away just because the company's name changed or disappeared from the news. If you are unsure who currently services your assessment, your county tax collector's records and your most recent tax bill are the places to check, not your roofing contractor.",
          },
          {
            type: "h3", text: "What about mis-selling claims" },
          {
            type: "p",
            text: "Separately, court-supervised settlement programs have existed for certain HERO borrowers who believe they were mis-sold, for example on income that was not properly verified or terms that were not clearly disclosed. If that describes your situation, that is a question for the settlement administrator or an attorney, not for a roofing and solar contractor. We can tell you the condition of your roof. We cannot tell you whether your loan was sold to you fairly.",
          },
        ],
        image: {
          src: solarPanelReplacement.url,
          alt: "Solar panels installed years ago under a PACE financing program that has since wound down",
          caption: "The financing company's fate and the hardware's condition are two separate questions.",
        },
      },
      {
        id: "is-pace-dead",
        h2: "Is PACE financing gone entirely now?",
        blocks: [
          {
            type: "p",
            text: "No, and this is worth being clear about because a lot of homeowners assume the whole category collapsed. What ended was HERO and Ygrene's era in the residential PACE market, not PACE itself. Active, regulated PACE programs still operate in California today under state oversight from the [California Public Utilities Commission](https://www.cpuc.ca.gov/) and the state's PACE Loss Reserve Program administered through the state treasurer's office.",
          },
          {
            type: "p",
            text: "We work with Renew Financial ourselves, alongside a full bench of loan and lease partners, and we will tell you honestly when PACE fits a project and when a conventional loan or lease makes more sense. PACE has real advantages in specific situations, mainly that it does not require the credit underwriting a personal loan does, and real drawbacks, mainly that it attaches to the property and can complicate a future sale. Those trade-offs did not change when HERO and Ygrene wound down; what changed is which company is offering them.",
          },
        ],
        image: {
          src: commercialSolar.url,
          alt: "Modern solar installation financed through a currently active, regulated PACE program",
          caption: "Active PACE programs still exist. The names attached to the paperwork are different than they were in 2018.",
        },
      },
      {
        id: "the-orphaned-roof-problem",
        h2: "What does the shutdown actually mean for the roof or system on my house?",
        blocks: [
          {
            type: "p",
            text: "Here is the practical problem, and it has nothing to do with your tax bill. The contractor who installed the PACE-financed roof, solar array, or HVAC system is very often gone too. PACE financing and the installer selling the job were frequently packaged together by the same sales channel, and when the financing arm's business slowed down, a lot of the installers riding along with it closed as well.",
          },
          {
            type: "p",
            text: "That leaves a roof or a solar system that nobody has inspected since it went up, sometimes since 2016 or 2018. Small problems have had years to grow: a boot that started to crack, flashing that lifted slightly, a valley that was never quite sealed right. None of that shows up on a property tax bill. It shows up as a water stain, eventually.",
          },
          {
            type: "ul",
            items: [
              "**If a roofer is gone**, any workmanship warranty they promised is only as good as the company behind it.",
              "**If a solar installer is gone**, the equipment warranty from the panel or inverter manufacturer usually survives, but filing a claim generally requires a licensed contractor to diagnose the failure.",
              "**If both are gone**, you are the only party left who knows the system exists, which is exactly why an inspection now is worth more than an inspection later.",
            ],
          },
          {
            type: "quote",
            text: "If you're still paying an assessment on a roof no one has looked at since 2018, an inspection is overdue, especially before adding solar or a battery to it.",
          },
        ],
        image: {
          src: solarInverterInstallation.url,
          alt: "Technician inspecting a solar inverter installed under a PACE-financed project years earlier",
          caption: "A system installed under financing that no longer exists still needs the same periodic checks every other system does.",
        },
      },
      {
        id: "what-we-do",
        h2: "What does McKay actually do for PACE-financed homes?",
        blocks: [
          {
            type: "p",
            text: "We hold one license, CSLB #1130793 (B, C-10, C-39), which means one crew can inspect and, if needed, work on the roof, the solar, and the electrical connecting them, without pointing you toward a second contractor for half the job.",
          },
          {
            type: "ol",
            items: [
              "A [roof inspection](/services/roof-inspection) with a written report, $399, credited in full toward any work if you hire us.",
              "[Repair or full replacement](/services/roof-replacement) for tile, shingle, and flat roofs, regardless of who originally installed them.",
              "Solar and battery service under one license, so a roof problem under a panel array does not become a two-contractor scheduling headache.",
              "Detach-and-reset work when a PACE-era roof needs attention underneath an existing solar array.",
            ],
          },
          {
            type: "p",
            text: "If your PACE-financed project included solar, our [solar panel repair](/services/solar-panel-repair) and [solar panel removal and reinstallation](/services/solar-panel-removal-reinstallation) services cover most of what an orphaned system needs.",
          },
        ],
        image: {
          src: heroInsuranceClaims.url,
          alt: "Written roof inspection report being handed to a homeowner in Southern California",
          caption: "A written report, not a verbal opinion, is what turns a vague worry into a documented condition.",
        },
      },
      {
        id: "checking-your-status",
        h2: "How do I check where my specific loan or assessment stands?",
        blocks: [
          {
            type: "p",
            text: "Start with your most recent county property tax bill, which will show the assessment line item and, in most cases, the name of the current servicer even if it is not Renovate America or Ygrene directly. Your county tax assessor's office can confirm the balance and payoff amount if you are selling or refinancing.",
          },
          {
            type: "p",
            text: "For anything involving a mis-selling claim or a settlement, an attorney or the settlement administrator named in your specific case is the right contact, not a contractor. The [California Department of Real Estate](https://www.dre.ca.gov/) and consumer protection resources maintained by the state are reasonable starting points if you are not sure where your paperwork stands.",
          },
          {
            type: "callout",
            title: "What we can and cannot tell you",
            text: "We can tell you the condition of the roof and whether the workmanship behind it held up. We cannot tell you your loan balance, your payoff figure, or whether you have a mis-selling claim. Those live with your county and, if it applies, your attorney.",
          },
        ],
        image: {
          src: solarInstallation.url,
          alt: "Homeowner and contractor reviewing a roof and solar system originally financed through a PACE program",
          caption: "Two separate conversations: one about the paperwork, one about the hardware. We only handle the second.",
        },
      },
      {
        id: "before-you-add-anything",
        h2: "Should I get an inspection before adding solar or a battery to a PACE-financed roof?",
        blocks: [
          {
            type: "p",
            text: "Yes, and this is the piece that gets skipped most often. Adding a [solar battery](/services/solar-battery-installation) or a [Tesla Powerwall](/services/tesla-powerwall-installation) to a roof nobody has checked in years means putting new equipment, new penetrations, and additional weight on a surface with an unknown, unverified condition.",
          },
          {
            type: "p",
            text: "It also matters for how you use the power once it is installed. SDG&E's current time-of-use pricing runs a peak window roughly from 4 to 9pm, and a battery or a well-sized solar system changes the math on that window considerably. Our [utility companies](/utility-companies) page breaks down what that peak pricing actually means for a San Diego County home.",
          },
          {
            type: "p",
            text: "If you are ready to have someone look at a PACE-financed roof or system honestly, [book a solar or roof inspection](/book/solar-repair) or [contact us](/contact) and we will tell you what we find, in writing, whether or not you hire us for the follow-up work.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Renovate America went bankrupt. Do I still owe my HERO assessment?",
        a: "Yes. The bankruptcy did not cancel homeowner assessments; they continue to be collected through your property tax bill. Questions about your specific balance go to your county tax assessment records or the servicer named on your bill.",
      },
      {
        q: "Can I still get PACE financing for a new roof or solar project?",
        a: "Yes. Active, regulated PACE programs exist in California today. We offer PACE through Renew Financial where it genuinely fits the project, with the trade-offs explained plainly before you sign anything.",
      },
      {
        q: "The company that installed my PACE-financed roof is gone. Is my workmanship warranty dead?",
        a: "A workmanship warranty is only as alive as the company behind it. If that company has closed, that promise typically has no one left to honor it. What you can control now is documented condition, which is what a written inspection gives you.",
      },
      {
        q: "Is McKay affiliated with HERO, Ygrene, or Renovate America?",
        a: "No. We are not affiliated with any of those companies and never have been. We are a licensed San Diego roofing and solar contractor that services homes regardless of how the original project was financed.",
      },
      {
        q: "What does the $399 inspection actually include?",
        a: "A full roof condition assessment with a written report covering flashing, penetrations, decking where accessible, and any solar-related mounting hardware. If you hire us for repair or replacement afterward, the full $399 is credited toward that work.",
      },
      {
        q: "Should I stop paying my PACE assessment since the company is gone?",
        a: "No. The assessment is tied to your property, not to the original company's continued existence, and it is generally still owed through your tax bill. Stopping payment without confirming your situation with your county or an attorney can create separate problems on top of the roof question.",
      },
    ],
    cta: {
      title: "Get a written inspection on a PACE-financed roof",
      text: "Whatever happened to HERO, Ygrene, or the contractor who did the original work, your roof still needs a periodic look from someone licensed to give one. Call (858) 541-1097 or book online for a $399 inspection, credited in full toward any work you hire us for.",
    },
    ctaType: "roofing",
  },
  {
    slug: "sunlight-financial-customers",
    title: "Sunlight Financial Bankruptcy: Your Loan Now | McKay",
    h1: "Sunlight Financial filed bankruptcy in 2023. What does that actually change for your solar loan?",
    metaDescription:
      "Sunlight Financial filed Chapter 11 in October 2023 and was acquired that December. Here is what changed for your loan and what to check on your system.",
    keywords:
      "Sunlight Financial bankruptcy, solar loan San Diego, orphaned solar system, solar panel repair, Titan Solar Power customers",
    category: "Solar Service",
    datePublished: "2026-09-06",
    dateModified: "2026-09-09",
    readMinutes: 7,
    hero: {
      src: solarPanelReplacement.url,
      alt: "Rooftop solar array in San Diego financed years earlier through a Sunlight Financial loan",
      caption: "Sunlight Financial arranged the paperwork behind thousands of San Diego solar loans. The company changed. The loan usually did not.",
    },
    intro: [
      "Between 2015 and 2023, Sunlight Financial was one of the biggest names most solar owners never actually noticed. It was the financing company standing behind the loan paperwork that thousands of installers put in front of homeowners, including a lot of homeowners in San Diego, without the Sunlight name ever showing up prominently on the sales pitch.",
      "On October 30, 2023, Sunlight filed for Chapter 11 bankruptcy protection. It was acquired by an investor group led by Greenbacker Capital along with a consortium of solar industry buyers, and it emerged from restructuring that December. For a homeowner making monthly payments, the whole thing likely happened without a single noticeable change to their statement, or with only a small one.",
      "This post covers what the bankruptcy did and did not change, and more importantly, what usually did change around the same time: the installer who sold you the Sunlight-financed system going out of business. Those are two separate problems, and only one of them requires a phone call to us.",
    ],
    tldr: [
      "Sunlight Financial filed Chapter 11 bankruptcy on October 30, 2023, and was acquired and restructured by December 2023.",
      "Your loan obligation almost certainly continued unchanged. Sunlight arranged loans; the loans themselves are typically owned by partner banks and serviced separately.",
      "The bigger risk is your original installer, not Sunlight. Companies like Titan Solar Power, Lumio, and SunPower's dealer network have closed, leaving Sunlight-financed systems with nobody to service them.",
      "A $399 written system health check, credited toward any repair, is the fastest way to find out if your [solar panel repair](/services/solar-panel-repair) is overdue.",
    ],
    sections: [
      {
        id: "what-happened-to-sunlight",
        h2: "What exactly happened to Sunlight Financial?",
        blocks: [
          {
            type: "p",
            text: "Sunlight Financial was a fintech-style lender that partnered with solar installers around the country rather than selling directly to homeowners. An installer's sales rep would present financing options at the kitchen table, and one of those options was frequently paperwork branded or processed through Sunlight, even if the homeowner mostly remembers the installer's name.",
          },
          {
            type: "p",
            text: "The company filed for Chapter 11 bankruptcy protection on October 30, 2023, following financial strain tied to rising interest rates and a slowdown in the residential solar financing market that hit several similar companies around the same period. It was acquired by an investor group led by Greenbacker Capital, along with a consortium of solar industry buyers, and emerged from restructuring in December 2023.",
          },
          {
            type: "callout",
            title: "Not affiliated, general information only",
            text: "McKay Roofing and Solar is not affiliated with Sunlight Financial and never was. We are a San Diego roofing, solar, and battery contractor, CSLB #1130793 (B, C-10, C-39), Tesla and Enphase certified. This page is general information, not legal or financial advice. Your loan documents control.",
          },
        ],
        image: {
          src: solarInverterInstallation.url,
          alt: "Solar inverter and panel wiring on a home originally financed through Sunlight Financial",
          caption: "The financing changed hands. The hardware on the roof stayed exactly where it was installed.",
        },
      },
      {
        id: "what-it-means-for-your-loan",
        h2: "Does the bankruptcy change what I owe or who I pay?",
        blocks: [
          {
            type: "p",
            text: "For almost everyone, no, and that surprises people. Sunlight arranged loans, but the loans themselves are typically owned by partner banks and serviced separately from Sunlight's own corporate structure. That means your payment obligation continued right through the bankruptcy filing and the acquisition, because the entity actually holding your loan was likely never Sunlight itself.",
          },
          {
            type: "p",
            text: "If anything changed on your end, it was probably cosmetic: the name printed on your monthly statement, or which servicing portal you log into to make a payment. That is worth confirming rather than assuming.",
          },
          {
            type: "ol",
            items: [
              "Check your most recent loan statement for the servicer's name.",
              "Pull your credit report if you are unsure who is reporting the loan.",
              "Do not stop paying while you figure it out. A missed payment affects your credit regardless of what happened to the original lender.",
            ],
          },
          {
            type: "p",
            text: "The [Consumer Financial Protection Bureau](https://www.consumerfinance.gov/) publishes general guidance on what happens to a loan when the original lender is acquired or restructured, if you want a source outside of us and outside of Sunlight.",
          },
        ],
        image: {
          src: commercialSolar.url,
          alt: "Homeowner reviewing a solar loan statement after a lender bankruptcy and restructuring",
          caption: "Check the servicer's name on your latest statement before assuming anything changed.",
        },
      },
      {
        id: "the-real-problem",
        h2: "So what is the actual problem for Sunlight-financed solar owners?",
        blocks: [
          {
            type: "p",
            text: "Here is the part that has nothing to do with Sunlight's balance sheet. Many of the installers who sold Sunlight-financed systems are themselves gone. Titan Solar Power, Lumio, SunPower's dealer network, and a long list of other installers that were active in the mid-2010s through early 2020s have closed or restructured out of the residential installation business entirely.",
          },
          {
            type: "p",
            text: "That leaves a homeowner paying faithfully on a loan for a system that nobody will service. The loan survives the installer's closure. The service relationship does not. If your inverter faults, your monitoring goes dark, or a panel underperforms, there is no longer a company on the other end of the original phone number.",
          },
          {
            type: "quote",
            text: "You can be left paying faithfully on a loan for a system nobody will service. The loan survives; the service relationship doesn't.",
          },
        ],
        image: {
          src: solarInstallation.url,
          alt: "Solar array with a monitoring display that has gone dark after the original installer closed",
          caption: "A quiet monitoring app usually means the account was tied to the installer, not to you.",
        },
      },
      {
        id: "what-we-do",
        h2: "What does McKay do for a Sunlight-financed system?",
        blocks: [
          {
            type: "p",
            text: "We work on orphaned solar systems regardless of who financed or installed them originally. One license covers the whole picture, which matters when a roof problem sits underneath a solar array.",
          },
          {
            type: "ul",
            items: [
              "A full system health check with a written report, $399, credited in full toward any repair if you hire us.",
              "Diagnostics and [solar panel repair](/services/solar-panel-repair) for panels, inverters, and wiring.",
              "Monitoring restoration so the app is registered under your own name instead of the closed installer's account.",
              "[Roof work](/services/roof-replacement) under the array, including detach-and-reset, handled on one license instead of coordinating two contractors.",
              "[Battery retrofits](/services/solar-battery-installation), including [Tesla Powerwall installation](/services/tesla-powerwall-installation), for owners who want to make the most of their existing array.",
            ],
          },
          {
            type: "p",
            text: "If your original installer also carried out roof penetrations for the mounting hardware, our [solar panel removal and reinstallation](/services/solar-panel-removal-reinstallation) service is usually the fastest way to check both the roof and the array in one visit.",
          },
        ],
        image: {
          src: solarPanelReplacement.url,
          alt: "Technician performing a solar system health check on panels originally financed through Sunlight Financial",
          caption: "Serial numbers and install paperwork are easiest to match to a warranty claim while they are still legible.",
        },
      },
      {
        id: "sdge-and-battery-economics",
        h2: "Does the current rate structure change what I should do with the system?",
        blocks: [
          {
            type: "p",
            text: "It can. Under SDG&E's current time-of-use pricing, the peak window generally runs from about 4 to 9pm, which is exactly when solar production is winding down for the day. A lot of the savings that used to come from straightforward solar production have shifted toward battery storage, which lets you use daytime production during the expensive evening hours instead of exporting it for a lower credit.",
          },
          {
            type: "p",
            text: "We will confirm what applies to your specific system and rate plan before recommending anything. Our [utility companies](/utility-companies) page walks through how SDG&E's current structure affects solar and battery decisions in more detail.",
          },
        ],
        image: {
          src: solarInverterInstallation.url,
          alt: "Solar inverter paired with a home battery installed to work with SDG&E time-of-use pricing",
          caption: "A battery retrofit can change the economics of a system that has been sitting on solar alone for years.",
        },
      },
      {
        id: "checking-your-servicer",
        h2: "How do I find out who services my loan today?",
        blocks: [
          {
            type: "p",
            text: "Start with your most recent monthly statement, which should name the current loan servicer even if it is not the original financing brand. If you cannot locate a statement, your credit report will list the account and the name currently reporting it.",
          },
          {
            type: "p",
            text: "For questions about the loan itself, rather than the equipment, the servicer named on your statement is the correct contact, not us. We can tell you whether the hardware is healthy. We cannot change your loan terms or your balance.",
          },
        ],
      },
      {
        id: "next-step",
        h2: "What should I do next if my installer is also gone?",
        blocks: [
          {
            type: "p",
            text: "If your original installer closed and you are unsure whether it was Titan Solar Power, Lumio, or another name from that era, the details matter less than the next step: get a licensed local contractor to look at the system before a small fault becomes a bigger repair.",
          },
          {
            type: "p",
            text: "[Book a solar inspection](/book/solar-repair) or [contact us](/contact) directly, and we will give you a written report on where the system actually stands, whether or not you decide to hire us for the repair afterward.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Do I still owe my Sunlight loan after the bankruptcy?",
        a: "Yes. The financing company's bankruptcy did not cancel borrower obligations. If your system doesn't work, fixing the system, not stopping payment, is the path that protects your credit.",
      },
      {
        q: "My installer is gone and my lender went through bankruptcy. Who do I call?",
        a: "A licensed local contractor. We service orphaned solar systems across San Diego County regardless of who financed or installed them originally.",
      },
      {
        q: "Is McKay affiliated with Sunlight Financial?",
        a: "No. We are not affiliated with Sunlight Financial and never have been. We work on systems financed through them purely as a licensed local contractor providing service.",
      },
      {
        q: "Will a battery help with my current electric bill?",
        a: "For many SDG&E customers on time-of-use plans with a 4 to 9pm peak window, yes, though it depends on your specific rate plan and system size. We confirm the numbers before recommending anything.",
      },
      {
        q: "What if I don't know who installed my system originally?",
        a: "That is common with systems that changed hands through financing company mergers or installer closures. A system health check will identify the equipment manufacturer, which is usually enough to trace the warranty even without the original paperwork.",
      },
      {
        q: "Does the $399 inspection cover roof penetrations from the mounting hardware?",
        a: "Yes. The system health check includes a look at every roof penetration the original installation crew made, in addition to panel, inverter, and wiring diagnostics.",
      },
    ],
    cta: {
      title: "Get your Sunlight-financed system checked",
      text: "Whatever happened to Sunlight Financial or the installer who sold you the system, the panels on your roof still need someone licensed to service them. Call (858) 541-1097 or book a system health check, $399, credited in full toward any repair.",
    },
    ctaType: "solar",
  },
  {
    slug: "swell-energy-customers",
    title: "Swell Energy Wound Down: Your Battery Now | McKay",
    h1: "Swell Energy wound down in 2024. Does your home battery still have a warranty, and who services it now?",
    metaDescription:
      "Swell Energy stopped installing and servicing home batteries in Southern California in 2024. Here is what survives and who to call for repairs now.",
    keywords:
      "Swell Energy shutdown, home battery service San Diego, virtual power plant, Tesla Powerwall repair, orphaned solar battery",
    category: "Solar Service",
    datePublished: "2026-09-02",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: commercialSolar.url,
      alt: "Home battery storage system in San Diego originally enrolled through the Swell Energy virtual power plant program",
      caption: "Swell no longer installs or services home batteries in Southern California. The battery on your wall still does.",
    },
    intro: [
      "Swell Energy did something a little different from most home battery companies. Instead of just selling you a battery and walking away, it enrolled your battery in a virtual power plant, a network of home batteries that the utility can call on during peak demand, paying homeowners for letting their stored power get tapped when the grid needed it most.",
      "Swell ran those virtual power plant programs with all three of California's investor-owned utilities: San Diego Gas & Electric, Southern California Edison, and PG&E. In May 2021, the company announced a battery offer aimed at 8,000 customers across Southern California, a program that put a meaningful number of Powerwalls, LG batteries, and Enphase systems on San Diego County roofs and garages.",
      "In August 2024, Swell said it was winding down its operations. Some later reporting has described it more as a restructuring away from consumer installation than a complete shutdown, and that distinction genuinely matters for legal and corporate purposes. It does not change the practical situation for a homeowner with a Swell-enrolled battery today: Swell no longer installs or services home batteries in Southern California, and if something on your system fails, you need a licensed local contractor.",
    ],
    tldr: [
      "Swell Energy announced it was winding down operations in August 2024, after running virtual power plant programs with SDG&E, SCE, and PG&E since at least 2021.",
      "Equipment warranties from Tesla, LG, Enphase, or SolarEdge are intact. Those companies are still operating and their warranties survive the installer's closure.",
      "The installation warranty, covering the electrical work and roof penetrations, came from Swell. That promise generally has no one left behind it now.",
      "A $399 inspection covers battery state of health, inverter checks, and monitoring restoration under your own name, credited toward any [solar battery](/services/solar-battery-installation) work you hire us for.",
    ],
    sections: [
      {
        id: "what-happened-to-swell",
        h2: "What actually happened to Swell Energy?",
        blocks: [
          {
            type: "p",
            text: "Swell Energy was a Los Angeles company built around virtual power plants: instead of a battery just sitting in your garage waiting for an outage, Swell enrolled it in a program where the utility could draw on stored power during periods of high demand, with the homeowner compensated for participating.",
          },
          {
            type: "p",
            text: "It ran those programs with San Diego Gas & Electric, Southern California Edison, and PG&E, and by May 2021 had announced a battery offer aimed at 8,000 customers across Southern California. In August 2024, Swell announced it was winding down its operations. Some subsequent coverage frames this as a restructuring away from consumer installation rather than a full corporate shutdown, a distinction that matters to lawyers and creditors but not to a homeowner whose battery just faulted.",
          },
          {
            type: "callout",
            title: "The plain fact",
            text: "Swell no longer installs or services home batteries in Southern California. If something on your system fails, you need a licensed local contractor, which is not legal advice, it is simply the current operating reality.",
          },
        ],
        image: {
          src: solarInstallation.url,
          alt: "Home battery storage system that was enrolled in a virtual power plant program before the operator wound down",
          caption: "A battery enrolled in a virtual power plant is still, physically, just a battery that needs maintenance.",
        },
      },
      {
        id: "what-you-still-have",
        h2: "What survives Swell's shutdown, and what doesn't?",
        blocks: [
          {
            type: "h3", text: "Your equipment warranty is intact" },
          {
            type: "p",
            text: "A Tesla Powerwall, LG, Enphase, or SolarEdge warranty is a promise from the manufacturer, not from Swell. Those companies are still operating, and those warranties survive whatever happened to the installer. The catch is procedural: filing a claim generally requires a licensed contractor to diagnose the failure, document it, pull the affected part, and handle the paperwork. We do that work for our customers, and in many cases can replace failed equipment at no cost to you under the manufacturer's warranty.",
          },
          { type: "h3", text: "Your installation warranty is a different story" },
          {
            type: "p",
            text: "The promise that covered the electrical work, the mounting hardware, and any roof penetrations came from the installer, in this case Swell. When the installer is gone, that promise usually has no one behind it. That is the practical reality, not legal advice, and it is the same pattern we see with every failed solar and battery installer.",
          },
          { type: "h3", text: "Your financing agreement continues" },
          {
            type: "p",
            text: "A loan or lease on the battery is a separate contract with a separate company. It does not cancel because the installer wound down. If you are unsure who holds yours, the paperwork from your original installation names the financing company.",
          },
          { type: "h3", text: "Your VPP enrollment is between you and the utility" },
          {
            type: "p",
            text: "We are a contractor, not a program administrator. Questions about whether you are still enrolled in the virtual power plant program or still being credited for participation go to SDG&E or SCE directly. What we can tell you is whether the hardware itself is healthy enough to be worth staying enrolled in the first place. General background on how these demand-response programs are regulated is available from the [CPUC](https://www.cpuc.ca.gov/).",
          },
        ],
        image: {
          src: solarInverterInstallation.url,
          alt: "Battery inverter and connector inspection on a home enrolled in a former Swell Energy virtual power plant program",
          caption: "The equipment warranty rides with the manufacturer. The installation warranty rode with Swell, and that one is gone.",
        },
      },
      {
        id: "the-quiet-problem",
        h2: "Why is a home battery a bigger risk to ignore than solar panels alone?",
        blocks: [
          {
            type: "p",
            text: "A home battery is not a set-it-and-forget-it appliance the way solar panels mostly are. Cells degrade over time, inverters fault, firmware falls behind, and the monitoring that was supposed to alert you tends to go quiet first, especially when the portal was set up under the installer's account instead of your own.",
          },
          {
            type: "p",
            text: "We regularly find batteries that have been sitting at partial capacity, or not cycling at all, for the better part of a year. The homeowner had no idea, because the app still opened and still showed a screen. It just wasn't showing the real state of the hardware behind it.",
          },
          {
            type: "quote",
            text: "The homeowner had no idea, because the app still opened and still showed a screen.",
          },
        ],
        image: {
          src: commercialSolar.url,
          alt: "Battery monitoring dashboard showing reduced capacity on a system that has not been inspected recently",
          caption: "A working app is not the same as a working battery. Only a physical inspection tells you which one you have.",
        },
      },
      {
        id: "what-we-do",
        h2: "What does McKay do for orphaned Swell battery systems?",
        blocks: [
          {
            type: "ul",
            items: [
              "A $399 system and roof inspection report, credited toward your job: battery state of health, inverter and connector inspection, a production audit if you also have solar, and a look at every roof penetration the original crew made, with findings in writing.",
              "Diagnostics and repair under an active C-10 electrical license: failed inverters, communication faults, rapid-shutdown issues, and the manufacturer warranty claims that go with them.",
              "Monitoring restoration: getting your [Tesla Powerwall](/services/tesla-powerwall-installation), Enphase, or SolarEdge portal registered under your own name so you can actually see what your system is doing.",
              "Detach-and-reset for reroofs: we hold roofing (C-39), general building (B), and electrical (C-10) licenses under one company, so one crew removes the array, replaces the roof, and reinstalls the system, without finger-pointing between two contractors.",
              "Adding or expanding storage: sizing more [solar battery](/services/solar-battery-installation) capacity onto an existing array, with NEM 3.0 economics in mind if your system is on the newer tariff.",
            ],
          },
          {
            type: "p",
            text: "If the roof under your array needs attention along with the battery itself, our [roof inspection](/services/roof-inspection) and [solar panel removal and reinstallation](/services/solar-panel-removal-reinstallation) services are built to handle both in a single visit.",
          },
        ],
        image: {
          src: solarPanelReplacement.url,
          alt: "McKay technician replacing failed battery equipment under a manufacturer warranty claim",
          caption: "Equipment claims are easiest to file while serial numbers and install paperwork are still straightforward to match.",
        },
      },
      {
        id: "was-your-installer-one-too",
        h2: "What if my solar installer closed too, not just Swell?",
        blocks: [
          {
            type: "p",
            text: "Plenty of Swell battery customers also have panels from a solar company that has since closed: Titan Solar Power, SunPower, PetersenDean, Sungevity, Sullivan Solar Power, Vivint Solar, SolarCity, and Freedom Forever are among the names we hear most often from San Diego homeowners in this situation.",
          },
          {
            type: "p",
            text: "The pattern is the same regardless of which company it was: the equipment warranty likely survives with the manufacturer, and the installation warranty likely does not survive the closure. A [solar panel repair](/services/solar-panel-repair) visit can sort out which category your specific problem falls into.",
          },
        ],
        image: {
          src: solarInstallation.url,
          alt: "Solar array from a closed installer paired with a battery from the also-defunct Swell Energy program",
          caption: "Two closed companies on one roof is more common in San Diego than most homeowners realize.",
        },
      },
      {
        id: "why-check-now",
        h2: "Why is it worth checking now rather than waiting?",
        blocks: [
          {
            type: "p",
            text: "Equipment claims are easiest to file while serial numbers, install paperwork, and part records are still straightforward to match to the manufacturer's system. A battery that has quietly lost capacity is also a battery that is not earning its keep, and if you are still making a loan or lease payment on it either way, that gap is money leaving your account for nothing.",
          },
          {
            type: "p",
            text: "The [U.S. Department of Energy](https://www.energy.gov/eere/solar/homeowners-guide-going-solar) publishes general homeowner guidance on solar and storage system maintenance if you want a source outside of us. One visit tells you where you actually stand, and the $399 report is credited in full toward any work you hire us for.",
          },
          {
            type: "p",
            text: "[Book a solar or battery inspection](/book/solar-repair) or [contact us](/contact) directly. Estimates are always free, no deposit, no pressure.",
          },
        ],
      },
    ],
    faqs: [
      {
        q: "Does Swell Energy still install or service home batteries in Southern California?",
        a: "No. Swell wound down its operations in August 2024 and no longer installs or services home batteries in Southern California. A licensed local contractor is needed for any repair or diagnostic work now.",
      },
      {
        q: "Is my Tesla Powerwall or LG battery warranty still valid?",
        a: "Generally yes. The equipment warranty is a promise from the manufacturer, not from Swell, and those companies are still operating. Filing a claim typically requires a licensed contractor to diagnose the failure and handle the paperwork.",
      },
      {
        q: "What happened to my virtual power plant enrollment and payments?",
        a: "That is a question for your utility, SDG&E or SCE, since the VPP program and any payments are between you and the utility, not the contractor servicing your equipment. We can only confirm whether the hardware is healthy enough to be worth staying enrolled.",
      },
      {
        q: "Do I still owe payments on my battery loan or lease?",
        a: "Yes. A loan or lease is a separate contract with a separate financing company, and it does not cancel because the installer wound down.",
      },
      {
        q: "Is McKay affiliated with Swell Energy?",
        a: "No. We are not affiliated with Swell Energy. We service Swell-installed battery systems purely as a licensed local contractor, CSLB #1130793 (B, C-10, C-39).",
      },
      {
        q: "What does the $399 inspection check on a battery system specifically?",
        a: "Battery state of health, inverter and connector inspection, a production audit if you also have solar, and a review of every roof penetration the original installer made, all delivered as a written report.",
      },
    ],
    cta: {
      title: "Get your Swell battery system checked",
      text: "Swell no longer installs or services home batteries in Southern California, but your Powerwall, LG, or Enphase battery still needs a licensed local contractor. Call (858) 541-1097 or book a $399 inspection, credited in full toward any work.",
    },
    ctaType: "solar",
  },
];
